import { RoomMainStatus, RoomDisabledType, MemberLevel } from '@/config/constant';
import { FutureTimeAction } from '@/redux/actions/roomState/constant';
import { DateType } from '@/utils/date';
import { ChannelItem } from '@/redux/reducers/reservation/base';

// 出租率
export interface RoomRateInfo {
  date: number; // 营业日
  fullRoomRate: string; // 满房率
  voidCount: number; // 剩余数量
  checkInNum: number; // 入住人数
}

// 房型数量
export interface CategoryCount {
  date: number; // 营业日
  occupiedCount: number; // 空房数量
  reservedCount: number; // 预订数量
  roomTypeId: number; // 房型id
  voidCount: number; // 剩余数量
  waitingRoomCount: number; // 待排房数量
}

// 房间状态
export interface RoomFutureStatus {
  date: number; // 营业日
  disableReason: RoomDisabledType; // 房间停用原因
  mainStatus: RoomMainStatus; // 房间状态
  masterName: string; // 房主姓名
  name: string; // 预订人姓名
  recNo: string; // 子单号
  resvNo: string; // 主单号
  roomId: number; // 房间id
  roomTypeId: number; // 房型id
  sourceChannelId: number; // 渠道id
  // memberFlag: boolean; // 是否会员
  memberLevel: MemberLevel; // 会员等级
  indexId: string; // 单元格id
  expectedArrivalTime?: number; // 预抵时间
  expectedDepartureTime?: number; // 预离时间
  timeRentFlag: boolean; // 是否存在钟点房
  timeRentExpectedArrivalTime?: number; // 钟点房预抵时间
  timeRentExpectedDepartureTime?: number; // 钟点房预离时间
}

// 房间信息
export interface RoomStatusInfo {
  roomId: number; // 房间id
  roomNo: string; // 房间号
  roomTypeId: number; // 房型id
  mainStatusDates: RoomFutureStatus[]; // 房间远期状态
}

// 房型数据
export interface CategoryItemInfo {
  enableTotal: number; // 启用数量
  roomTypeId: number; // 房型id
  roomTypeName: string; // 房型名称
  mainStatusCountDates: CategoryCount[]; // 房间远期数量信息
  roomStatusFutures: RoomStatusInfo[]; // 房间远期状态信息
}

// 房态数据
interface FutureRoomData {
  fullRoomRateDates: RoomRateInfo[]; // 所有日期出租率
  roomTypeFutures: CategoryItemInfo[];
  total: number; // 房间总数
}

// 时间范围
export interface RangeOption {
  startDate: DateType; // 开始时间
  endDate: DateType; // 截至日期
}

export interface ReducerState {
  isLoading: boolean; // 加载状态
  businessDate: number; // 营业日
  nightAuditTime: number; // 夜审时间
  channelList: ChannelItem[]; // 渠道列表
  calendarRange: [DateType, DateType]; // 筛选时间范围
  futureRoomData: FutureRoomData; // 房态数据
  foldTypeList: number[]; // 收起的房型列表
  allFoldStatus: boolean; // 是否全部收起
}

const initialState: ReducerState = {
  isLoading: true,
  businessDate: Date.now(),
  nightAuditTime: null,
  channelList: [], // 渠道列表
  calendarRange: null,
  futureRoomData: {
    fullRoomRateDates: [],
    roomTypeFutures: [],
    total: 0,
  },
  foldTypeList: [],
  allFoldStatus: false,
};

type ActionHandler<T> = (state: ReducerState, action: { payload: T }) => ReducerState;

const reducerMap = {
  [FutureTimeAction.setLoadingStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return {
      ...state,
      isLoading: payload,
    };
  }),
  [FutureTimeAction.setBusinessDate]: <ActionHandler<number>>((state, { payload }) => {
    return {
      ...state,
      businessDate: payload,
    };
  }),
  [FutureTimeAction.setNightAuditTime]: <ActionHandler<number>>((state, { payload }) => {
    return {
      ...state,
      nightAuditTime: payload,
    };
  }),
  [FutureTimeAction.setChannelList]: <ActionHandler<ChannelItem[]>>((state, { payload }) => {
    return {
      ...state,
      channelList: payload,
    };
  }),
  [FutureTimeAction.setCalendarDateRange]: <ActionHandler<[DateType, DateType]>>((
    state,
    { payload },
  ) => {
    return {
      ...state,
      calendarRange: payload,
    };
  }),
  [FutureTimeAction.setFutureRoomData]: <ActionHandler<FutureRoomData>>((state, { payload }) => {
    return {
      ...state,
      futureRoomData: payload,
    };
  }),
  [FutureTimeAction.setFoldRoomData]: <ActionHandler<number[]>>((state, { payload }) => {
    const { futureRoomData } = state;
    const allFoldStatus = futureRoomData.roomTypeFutures.every((item) =>
      payload.includes(item.roomTypeId),
    );
    return {
      ...state,
      foldTypeList: payload,
      allFoldStatus,
    };
  }),
};

export default (state = initialState, action) => {
  const actionType = action.type;
  const reducer = reducerMap[actionType];
  if (typeof reducer === 'function') {
    return reducer(<ReducerState>state, action);
  }
  return state;
};

// redux store name
export const storeName = 'futureState';
